<!--
  This dialog will opened when ops get the customer info on RightPanel.
-->
<template>
  <el-dialog
    class="dlg-notes-for-caller page-content"
    :model-value="customerNotesDialogVisible"
    :before-close="handleClose"
    :close-on-press-escape="false"
    :show-close="!delayTime ? true : false"
    :close-on-click-modal="false"
  >
    <template #header>
      <span class="el-dialog__title">PLEASE READ</span>
      <button
        type="button"
        aria-label="Refresh"
        class="el-dialog__headerbtn refresh-btn"
        @click="redirectPage"
      >
        <i class="el-dialog__close el-icon el-icon-refresh-right" />
      </button>
    </template>

    <div class="row">
      <div class="col-6">
        <label class="control-label section-title pl-0">Customer Plan</label>
        <div
          v-if="planDisplayName"
          class="content-view"
        >
          <span>{{ planDisplayName }}</span>
          <br>
          <span>({{ planPrice }})</span>
        </div>
      </div>

      <div class="col-6">
        <label class="control-label section-title pl-0">Total Free Rides</label>
        <div
          v-if="!!totalOrderInfo"
          class="content-view"
        >
          {{ totalOrderInfo.totalFreeRides }}
        </div>
      </div>
    </div>

    <div
      v-if="isUnpaidRidesShow"
      class="row"
    >
      <div class="col-12 warning-block">
        <label class="control-label section-title pl-0">Unpaid Rides</label>
        <div class="content-view">
          <ul class="mb-0">
            <li
              v-for="(ride, index) in unpaidInfo.unpaidRides"
              :key="index"
            >
              <span>{{ ride.requestId }}</span>
              <span class="unpaidCost">{{ formatUnpaidCost(ride.unpaidCost) }}</span>
            </li>
          </ul>
        </div>
      </div>
    </div>

    <div
      v-if="isUnpaidGourmetOrdersShow"
      class="row"
    >
      <div class="col-12 warning-block">
        <label class="control-label section-title pl-0">Unpaid Gourmet Orders</label>
        <div class="content-view">
          <ul class="mb-0">
            <li
              v-for="(order, index) in unpaidInfo.unpaidGourmetOrders"
              :key="index"
            >
              <span>{{ order.title }}</span>
              <span class="unpaidCost">{{ formatUnpaidCost(order.unpaidCost) }}</span>
            </li>
          </ul>
        </div>
      </div>
    </div>

    <div
      v-if="isUnpaidGroceriesShow"
      class="row"
    >
      <div class="col-12 warning-block">
        <label class="control-label section-title pl-0">Unpaid Grocery Orders</label>
        <div class="content-view">
          <ul class="mb-0">
            <li
              v-for="(grocery, index) in unpaidInfo.unpaidGroceries"
              :key="index"
            >
              <span>{{ grocery.orderId }}</span>
              <span class="unpaidCost">{{ formatUnpaidCost(grocery.unpaidCost) }}</span>
            </li>
          </ul>
        </div>
      </div>
    </div>

    <template v-if="isTemporaryNotesShow">
      <hr>
      <div class="row">
        <div class="col-12">
          <label class="control-label section-title pl-0">Temporary Notes</label>
          <div
            v-for="(item, index) in temporaryNotes"
            :key="index"
            class="temporary-notes-container"
          >
            <label class="control-label pl-0">
              Temporary Notes{{ index + 1 }}&nbsp; (Expires At:
              {{ util.formatDateTime(item.expiredAt, 'YYYY-MM-DD HH:mm', customerInfo.timeZone) }})
            </label>
            <div
              v-dompurify-html="util.convertTextToHtml(item.notes ?? '', true)"
              class="temporary-note"
            />
          </div>
        </div>
      </div>
    </template>

    <div class="row">
      <div class="col-12">
        <label class="control-label section-title pl-0">Management Note</label>
        <div
          v-dompurify-html="notesForCustomer.managementNoteView"
          class="content-view mb-10"
        />
        <span
          v-if="notesForCustomer.managementNoteView"
          class="notification-message"
        >
          Please keep confidential, do not read to customers or loved ones!
        </span>
      </div>
    </div>

    <hr>
    <div class="row">
      <div class="col-12">
        <label class="control-label section-title pl-0">Operator Note</label>
        <div>
          <sanitized-textarea
            v-model="notesForCustomer.operatorNote"
            class="operator-note"
            :class="isAnimation ? 'animation-section' : ''"
          />
          <span class="notification-message"> Please keep confidential, do not read to customers or loved ones! </span>
        </div>
      </div>
    </div>

    <template #footer>
      <div class="dialog-footer">
        <div
          class="delay-time"
          :class="{ 'mobile-pb-0': !delayTime }"
        >
          {{ delayTime ? `Please wait ${delayTime} seconds` : '' }}
        </div>
        <div class="form-submit-button-group">
          <p-button
            id="btnSave"
            type="purple"
            round
            :disabled="!!delayTime"
            @click="updateNotes"
          >
            Save
          </p-button>

          <p-button
            id="btnCancel"
            type="yellow"
            round
            :disabled="!!delayTime"
            @click="handleClose"
          >
            Cancel
          </p-button>
        </div>
      </div>
    </template>
  </el-dialog>
</template>

<script lang="ts">
import { ref, Ref, toRefs, reactive, watch, computed, onBeforeUnmount } from 'vue';
import { useRouter } from 'vue-router';
import events from '@/events';

import util from '@/Utils/util';
import { useCustomer } from '@/use/customer';
import { useProfile } from '@/use/profile';
import { useGlobal } from '@/use/global';

export default {
  setup: () => {
    const router = useRouter();
    const { userRoles } = useProfile();

    const { customerInfo, customerPhoneNumber, customerPlans, totalOrderInfo, customerNotesDialogVisible, setCustomerNotesDialogVisible } =
      useCustomer();
    const { planDisplayName, planPrice } = toRefs(customerPlans);

    const { temporaryNotes } = useGlobal();

    const notesForCustomer = reactive({
      operatorNote: '',
      managementNoteView: '',
    });

    const unpaidInfo = reactive({
      unpaidGourmetOrders: [],
      unpaidRides: [],
      unpaidGroceries: [],
    });

    const delayTime = ref(0);
    const notesIntervalID: Ref<any> = ref(null);

    const isAnimation = computed(() => {
      const operatorNote = customerInfo.value?.operatorNote || '';
      return operatorNote.toLowerCase().includes('account update required');
    });

    const isUnpaidRidesShow = computed(() => {
      return unpaidInfo.unpaidRides.length > 0;
    });

    const isUnpaidGourmetOrdersShow = computed(() => {
      return unpaidInfo.unpaidGourmetOrders.length > 0;
    });

    const isUnpaidGroceriesShow = computed(() => {
      return unpaidInfo.unpaidGroceries.length > 0;
    });

    const isTemporaryNotesShow = computed(() => {
      return temporaryNotes.value.length > 0;
    });

    const updateNotes = () => {
      setCustomerNotesDialogVisible(false);
      events.emit('update-customer-operator-notes', {
        operatorNote: notesForCustomer.operatorNote,
      });
    };

    const handleClose = () => {
      setCustomerNotesDialogVisible(false);
    };

    const redirectPage = () => {
      if (notesIntervalID.value) {
        clearInterval(notesIntervalID.value);
      }

      router.push('/');
    };

    const hasSkipTimerPermission = computed(() => {
      return (
        userRoles.isAdmin ||
        userRoles.isSupervisor ||
        userRoles.isDirector ||
        userRoles.isTeamLead ||
        userRoles.isLossPrevention ||
        userRoles.isLossPreventionGourmet ||
        userRoles.isLossPreventionGrocery ||
        userRoles.isVirtualAssistanceGrocery
      );
    });

    const formatUnpaidCost = (cost: number) => {
      if (cost === 0) {
        return 'Waiting for receipt';
      }

      return `$${cost}`;
    };

    watch(customerInfo, (value: any) => {
      notesForCustomer.managementNoteView = util.convertTextToHtml(value.managementNote);
      notesForCustomer.operatorNote = value.operatorNote;

      unpaidInfo.unpaidGourmetOrders = value?.unpaidGourmetOrders || [];
      unpaidInfo.unpaidRides = value?.unpaidRides || [];
      unpaidInfo.unpaidGroceries = value?.unpaidGroceries || [];
    });

    watch(customerNotesDialogVisible, (value: boolean) => {
      if (!value) {
        return;
      }

      delayTime.value = 0;
      const isSameCustomerAsLastPulledUpCustomer = customerPhoneNumber.value !== customerInfo.value.lastCXPhoneNumber;

      if (!hasSkipTimerPermission.value && isSameCustomerAsLastPulledUpCustomer) {
        const characterLength = customerInfo.value.managementNote?.length || 0;

        if (characterLength) {
          delayTime.value = Math.min(5, Math.ceil(characterLength / 20));
          notesIntervalID.value = window.setInterval(() => {
            delayTime.value--;

            if (!delayTime.value && notesIntervalID.value) {
              notesIntervalID.value = clearInterval(notesIntervalID.value);
            }
          }, 1000);
        }
      }
    });

    onBeforeUnmount(() => {
      if (notesIntervalID.value) {
        clearInterval(notesIntervalID.value);
      }
    });

    return {
      customerInfo,
      customerNotesDialogVisible,
      isAnimation,
      isUnpaidRidesShow,
      isUnpaidGourmetOrdersShow,
      isUnpaidGroceriesShow,
      temporaryNotes,
      isTemporaryNotesShow,
      delayTime,
      notesForCustomer,
      unpaidInfo,
      planDisplayName,
      planPrice,
      totalOrderInfo,

      util,

      updateNotes,
      handleClose,
      redirectPage,
      formatUnpaidCost,
    };
  },
};
</script>

<style lang="scss">
.dlg-notes-for-caller {
  .el-dialog__header {
    text-align: center;
    .el-dialog__headerbtn.refresh-btn {
      right: 50px;
    }
  }
  .dialog-footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    @media (max-width: 576px) {
      flex-direction: column;
      .form-submit-button-group {
        width: 100%;
      }
    }
    .delay-time {
      font-size: 16px;
      font-weight: bold;
      color: #d03b3b;
      @media (max-width: 576px) {
        padding-bottom: 15px;
      }
    }
  }
  label.section-title {
    font-weight: bold;
  }
  .animation-section {
    animation: border-effect 1s infinite;
  }
  .warning-block {
    color: red;
    .control-label {
      color: red;
    }
    .content-view {
      .unpaidCost {
        margin-left: 20px;
        font-weight: bold;
      }
    }
  }
  .temporary-note {
    white-space: break-spaces;
  }
  @keyframes border-effect {
    from {
      border: 3px solid #f00;
    }
    to {
      border: 3px solid #fff;
    }
  }
  .temporary-notes-container {
    padding-left: 10px;
    padding-bottom: 10px;
    .control-label {
      color: #ea6a39;
    }
    &:last-child {
      padding-bottom: 0;
    }
  }
}
</style>
